import { useEffect, type ReactElement, type ReactNode } from 'react';
import { type ApiClient } from '../api/client';
import { authStore } from '../auth/auth-store';
import { sessionStore } from './session-store';

export interface SessionProviderProps {
  children: ReactNode;
  client?: ApiClient;
  /** Load `/api/v1/identity/me` whenever the auth status becomes `authenticated` (default true). */
  autoLoad?: boolean;
}

/**
 * Loads the session (`GET /api/v1/identity/me`) as soon as the user is authenticated, hydrates
 * user/roles/permissions from id-token claims meanwhile, and clears it on sign-out.
 */
export function SessionProvider({ children, client, autoLoad = true }: SessionProviderProps): ReactElement {
  const status = authStore((s) => s.status);
  const claims = authStore((s) => s.claims);

  useEffect(() => {
    const session = sessionStore.getState();
    if (status === 'authenticated') {
      session.hydrateFromClaims(claims);
      if (autoLoad && session.status !== 'ready' && session.status !== 'loading') {
        void session.load(client);
      }
    } else if (status === 'anonymous' || status === 'error') {
      if (session.status !== 'idle') session.clear();
    }
  }, [status, claims, client, autoLoad]);

  return <>{children}</>;
}
